import React, { useContext, useEffect, useRef, useState } from "react";
import styles from "./ExplorerWidget.module.css";
import ExplorerWidgetPlayer from "./ExplorerWidgetPlayer";
import TransactionModal from "./TransactionModal";
import GuiContext from "config/context/guiContext";
import useTheme from "config/useTheme";
import WidgetShell from "../WidgetShell";

// Temporary solution
const ExplorerWidget = () => {
  const componentRef = useRef(null);

  const { dark } = useTheme();
  const { URL, selected: { selectedServer } } = useContext(GuiContext);

  const [modalToggle, setModalToggle] = useState();
  const handleModalToggle = () => {
    setModalToggle(!modalToggle);
  };

  const [blocks, setBlocks] = useState([]);
  const [previewBlock, setPreviewBlock] = useState({});
  const [transactionData, setTransactionData] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const pause = useRef(false);
  const live = useRef(true);

  const setPlayerPause = (value) => {
    pause.current = value;
  };

  const setPlayerLive = (value) => {
    live.current = value;
  };

  useEffect(() => {
    if (!live.current) return;
    const timeout = loadInfo(URL, selectedServer, setBlocks, setPreviewBlock, pause);

    return () => {
      timeout.then((timeout) => {
        clearInterval(timeout);
      });
    };
  }, [live.current, URL, selectedServer, pause]);

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);
  };

  const submitSearch = () => {
    const numberRegex = /^\d{1,20}$/;
    setErrMsg("");
    if (numberRegex.test(searchValue)) {
      searchBlock(URL, selectedServer, parseInt(searchValue), setPreviewBlock);
    } else {
      setErrMsg("Enter valid block ID");
    }
  };

  return (
    <WidgetShell colSpan={2}>
      <div
        className={`${styles.component} ${dark ? styles.dark : ""}`}
        ref={componentRef}
      >
        <div className={styles.container}>
          <div className={styles.block_info_area}>
            <div className={styles.block_search_box}>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  submitSearch();
                }}
              >
                <input
                  type="text"
                  placeholder="Block"
                  name="code"
                  value={searchValue}
                  onChange={handleSearchChange}
                />
                <button type="submit">Search</button>
              </form>
            </div>
            <div className={styles.search_errmsg}>
              <p>{errMsg}</p>
            </div>
            <div className={styles.block_info}>
              <h5>Block Info</h5>
              <div className={styles.block_info_data}>
                <p>
                  Master: <span>{previewBlock?.master}</span>
                </p>
                <p>
                  ID: <span>{previewBlock?.block_num}</span>
                </p>
                <p>
                  Time:{" "}
                  <span>
                    {new Date(previewBlock?.timestamp).toLocaleString()}
                  </span>
                </p>
              </div>
              <div className={styles.block_transactions}>
                <h6>Transactions:</h6>
                <div className={styles.block_transactions_box}>
                  {previewBlock?.transactions?.length ? (
                    previewBlock?.transactions?.map((transaction, id) => {
                      return (
                        <button
                          key={id}
                          onClick={() => {
                            handleModalToggle();
                            setTransactionData(transaction);
                          }}
                        >
                          {id + 1}
                        </button>
                      );
                    })
                  ) : (
                    <p>No transactions data</p>
                  )}
                </div>
                {modalToggle ? (
                  <TransactionModal
                    toggle={handleModalToggle}
                    data={transactionData}
                  />
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
          <div className={styles.block_live_area}>
            <ExplorerWidgetPlayer
              blocks={blocks}
              setPreview={setPreviewBlock}
              setPlayerPause={setPlayerPause}
              setPlayerLive={setPlayerLive}
            />
          </div>
        </div>
      </div>
    </WidgetShell>
  );
};

const loadInfo = async (url, serverUrl, setBlocks, setPreviewBlock, pause) => {
  try {
    const chainInfo = await fetch(`${url}/api/utils/info`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        server_url: serverUrl,
      }),
    });
    const headBlockNum = (await chainInfo.json()).data.head_block_num;
    if (!headBlockNum) return;
    return loadBlocks(
      url,
      serverUrl,
      headBlockNum,
      setBlocks,
      setPreviewBlock,
      pause
    );
  } catch (error) { }
};

const loadBlocks = async (
  url,
  serverUrl,
  headBlockNum,
  setBlocks,
  setPreviewBlock,
  pause
) => {
  setBlocks([]);
  for (
    let i = headBlockNum > 5 ? headBlockNum - 5 : 1;
    i <= headBlockNum;
    i++
  ) {
    try {
      const result = await fetch(`${url}/api/utils/block`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          server_url: serverUrl,
          block_number: i,
        }),
      });
      const blockInfo = await result.json();
      setBlocks((prevState) => [...prevState, blockInfo.data]);
      if (i == (headBlockNum > 5 ? headBlockNum - 5 : 1))
        setPreviewBlock(JSON.parse(JSON.stringify(blockInfo.data)));
    } catch (error) { }
  }
  let headBlock = headBlockNum + 1;
  const startFetching = async () => {
    try {
      if (pause.current) return;
      const result = await fetch(`${url}/api/utils/block`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          server_url: serverUrl,
          block_number: headBlock,
        }),
      });
      const blockInfo = await result.json();
      setBlocks((prevState) => {
        if (!prevState.length) return [blockInfo.data];
        let oldBlocks = prevState;
        if (oldBlocks[oldBlocks.length - 1].block_num < headBlock) {
          if (prevState.length >= 100) oldBlocks = prevState.slice(1);
          return [...oldBlocks, blockInfo.data];
        }
        return oldBlocks;
      });
      headBlock++;
    } catch (error) { }
  };
  return timeoutInterval(startFetching, 500);
};

const searchBlock = async (url, serverUrl, blockId, setPreviewBlock) => {
  try {
    const result = await fetch(`${url}/api/utils/block`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        server_url: serverUrl,
        block_number: blockId,
      }),
    });
    const blockInfo = await result.json();
    if (result.ok) {
      setPreviewBlock(blockInfo.data);
    }
  } catch (error) { }
};

const timeoutInterval = (callback, intervalTime) => {
  return setInterval(callback, intervalTime);
};

export default ExplorerWidget;
